import type { DiagramDocument, DiagramEdge, DiagramNode } from '../../core/types';
import { normalizeDocument } from '../../core/normalize';
import { validateDocument } from '../../core/validation';
import type { LiveOperation } from './liveOperations';

export interface ServerUser {
  id: string;
  email: string;
  role: 'master' | 'admin' | 'user' | string;
  displayName?: string;
  createdAt?: string | null;
}

export interface ServerOrganization {
  id: string;
  name: string;
  slug: string;
  role: 'owner' | 'admin' | 'member' | string;
  planCode?: string;
  planStatus?: string;
  planExpiresAt?: string | null;
}

export interface ServerSession {
  token: string;
  expiresAt: string;
  user: ServerUser;
}




export interface ServerRevisionSummary {
  revision: string;
  localRevision: string;
  title: string;
  createdAt: string;
  email: string;
}

export interface AuditEvent {
  id: string;
  action: string;
  createdAt: string;
  email: string | null;
  metadata: Record<string, unknown>;
}

export interface ProjectComment {
  id: string;
  nodeId: string | null;
  body: string;
  createdAt: string;
  resolvedAt: string | null;
  userId: string;
  email: string;
}

export interface ProjectMember {
  userId: string;
  email: string;
  permission: 'view' | 'comment' | 'edit';
}

export interface PublicShareSummary {
  id: string;
  allowCopy: boolean;
  allowPdf: boolean;
  createdAt: string;
  expiresAt: string | null;
  views: number;
}

export interface PublicSharePayload {
  title: string;
  document: DiagramDocument;
  allowCopy: boolean;
  allowPdf: boolean;
  expiresAt: string | null;
  ownerLabel: string | null;
}


export interface AdminPlan {
  code: string;
  name: string;
  description: string;
  monthlyPriceCents: number | null;
  annualPriceCents: number | null;
  maxUsers: number;
  maxProjects: number;
  maxStorageBytes: number;
  features: string[];
  active: boolean;
  system: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOverview {
  stats: { users: number; organizations: number; projects: number; sessions: number; revisions: number; comments: number; templates: number; announcements: number; storageBytes: number };
  organizations: Array<{ id: string; name: string; slug: string; createdAt: string; members: number; projects: number; storageBytes: number; maxUsers: number; maxProjects: number; maxStorageBytes: number; planCode: string; planStatus: string; planExpiresAt: string | null; administratorRole?: 'owner'|'admin'|'master'|string }>;
  users: Array<{ id: string; email: string; displayName: string; role: string; enabled: boolean; createdAt: string; organizationId: string; organizationName: string; organizationRole: 'owner'|'admin'|'member'|string }>;
  audit: Array<{ id: string; organizationId: string; organizationName: string | null; action: string; createdAt: string; email: string | null; projectTitle: string | null; metadata: Record<string, unknown> }>;
}

export interface RemoteProjectSummary {
  id: string;
  title: string;
  revision: string;
  created_at: string;
  updated_at: string;
  owner_user_id: string;
  organization_name?: string | null;
  organization_id?: string | null;
  organization_role?: 'owner' | 'admin' | 'member' | string | null;
  deleted_at?: string | null;
  purgeAt?: string | null;
  permission: 'owner' | 'edit' | 'comment' | 'view';
  favorite?: boolean;
  folder?: string;
  tags?: string[];
  /** Projeto ainda existe apenas no armazenamento local deste usuário/dispositivo. */
  localOnly?: boolean;
  /** Estado visual de persistência usado pela Home; não vem da API. */
  syncState?: 'cloud' | 'pending' | 'local';
}

export interface AccountInfo {
  user: ServerUser;
  sessions: Array<{ id: string; createdAt: string; expiresAt: string; current: boolean }>;
  organizations: Array<ServerOrganization & { maxUsers: number; maxProjects: number; maxStorageBytes: number; users: number; projects: number; storageBytes: number; planName?: string | null; planDescription?: string | null; planMonthlyPriceCents?: number | null; planAnnualPriceCents?: number | null; planFeatures?: string[] }>;
}

export interface UserNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  projectId: string | null;
  createdAt: string;
  readAt: string | null;
  actorEmail: string | null;
  metadata: Record<string, unknown>;
}

export interface Announcement {
  id: string;
  title: string;
  body: string;
  level: 'info' | 'success' | 'warning' | 'critical';
  dismissible: boolean;
  startsAt: string;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  organizationName: string | null;
}

export interface AdminAnnouncement extends Announcement {
  organizationId: string | null;
  active: boolean;
  createdByEmail: string;
  seenCount: number;
  dismissedCount: number;
}


export interface OrganizationTemplate {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByEmail: string;
  canDelete: boolean;
  template: { nodes: DiagramNode[]; edges: DiagramEdge[] };
}

function safeBaseUrl(value: string): string {
  const url = new URL(value.trim());
  const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  if (url.protocol !== 'https:' && !local) throw new Error('Use HTTPS para servidor OWDEX fora deste computador.');
  return url.toString().replace(/\/$/, '');
}

async function readPayload(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  if (!text) return {};
  try { return JSON.parse(text) as Record<string, unknown>; }
  catch { throw new Error(`Resposta inválida do servidor (${response.status}).`); }
}

async function ensureOk(response: Response): Promise<Record<string, unknown>> {
  const payload = await readPayload(response);
  if (!response.ok) {
    const message = typeof payload.error === 'string' ? payload.error : `Falha no servidor (${response.status}).`;
    throw new Error(message);
  }
  return payload;
}

function parseRemoteProjects(value: unknown): RemoteProjectSummary[] {
  return Array.isArray(value) ? value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    if (![row.id,row.title,row.revision,row.created_at,row.updated_at,row.owner_user_id,row.permission].every((entry) => typeof entry === 'string')) return [];
    return [{ id: row.id as string, title: row.title as string, revision: row.revision as string, created_at: row.created_at as string, updated_at: row.updated_at as string, owner_user_id: row.owner_user_id as string, permission: row.permission as RemoteProjectSummary['permission'], organization_name: typeof row.organization_name === 'string' ? row.organization_name : null, organization_id: typeof row.organization_id === 'string' ? row.organization_id : null, organization_role: typeof row.organization_role === 'string' ? row.organization_role : null, deleted_at: typeof row.deleted_at === 'string' ? row.deleted_at : null, favorite: row.favorite === true || row.favorite === 1, folder: typeof row.folder === 'string' ? row.folder : '', tags: Array.isArray(row.tags) ? row.tags.filter((tag): tag is string => typeof tag === 'string') : [] }];
  }) : [];
}

function parseAnnouncement(value: unknown): Announcement[] {
  if (!value || typeof value !== 'object') return [];
  const row = value as Record<string, unknown>;
  if (![row.id,row.title,row.body,row.level,row.startsAt,row.createdAt,row.updatedAt].every((entry) => typeof entry === 'string')) return [];
  if (!['info','success','warning','critical'].includes(String(row.level))) return [];
  return [{ id: row.id as string, title: row.title as string, body: row.body as string, level: row.level as Announcement['level'], dismissible: row.dismissible === true, startsAt: row.startsAt as string, endsAt: typeof row.endsAt === 'string' ? row.endsAt : null, createdAt: row.createdAt as string, updatedAt: row.updatedAt as string, organizationName: typeof row.organizationName === 'string' ? row.organizationName : null }];
}

function parseAdminAnnouncement(value: unknown): AdminAnnouncement[] {
  const parsed = parseAnnouncement(value);
  if (parsed.length === 0 || !value || typeof value !== 'object') return [];
  const row = value as Record<string, unknown>;
  return [{ ...parsed[0], organizationId: typeof row.organizationId === 'string' ? row.organizationId : null, active: row.active === true, createdByEmail: typeof row.createdByEmail === 'string' ? row.createdByEmail : '', seenCount: Number(row.seenCount) || 0, dismissedCount: Number(row.dismissedCount) || 0 }];
}

export class ServerApi {
  readonly baseUrl: string;
  constructor(baseUrl: string) { this.baseUrl = safeBaseUrl(baseUrl); }

  async applyLiveOperations(token: string, projectId: string, localRevision: string, knownRevision: string | null, operations: LiveOperation[], signal?: AbortSignal): Promise<{ revision: string; concurrent: boolean }> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/operations`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ localRevision, knownRevision, operations }), credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    if (typeof payload.revision !== 'string') throw new Error('Servidor não retornou revisão para as operações.');
    return { revision: payload.revision, concurrent: payload.concurrent === true };
  }

  async health(signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/health`, { cache: 'no-store', credentials: 'omit', signal });
    const payload = await ensureOk(response);
    if (payload.status !== 'ok') throw new Error('Servidor OWDEX Draw não respondeu como esperado.');
  }

  async login(email: string, password: string, signal?: AbortSignal): Promise<ServerSession> {
    const response = await fetch(`${this.baseUrl}/v1/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), password }),
      credentials: 'omit', cache: 'no-store', signal,
    });
    const payload = await ensureOk(response);
    if (typeof payload.token !== 'string' || typeof payload.expiresAt !== 'string' || !payload.user || typeof payload.user !== 'object') throw new Error('Sessão inválida retornada pelo servidor.');
    const user = payload.user as Record<string, unknown>;
    if (typeof user.id !== 'string' || typeof user.email !== 'string' || typeof user.role !== 'string') throw new Error('Usuário inválido retornado pelo servidor.');
    return { token: payload.token, expiresAt: payload.expiresAt, user: { id: user.id, email: user.email, role: user.role } };
  }

  async logout(token: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/auth/logout`, { method: 'POST', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    if (response.status !== 401) await ensureOk(response);
  }

  async me(token: string, signal?: AbortSignal): Promise<{ user: ServerUser; organizations: ServerOrganization[] }> {
    const response = await fetch(`${this.baseUrl}/v1/me`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    const user = payload.user as Record<string, unknown> | undefined;
    if (!user || typeof user.id !== 'string' || typeof user.email !== 'string' || typeof user.role !== 'string') throw new Error('Perfil inválido retornado pelo servidor.');
    const organizations = Array.isArray(payload.organizations) ? payload.organizations.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const row = item as Record<string, unknown>;
      return typeof row.id === 'string' && typeof row.name === 'string' && typeof row.slug === 'string' && typeof row.role === 'string'
        ? [{ id: row.id, name: row.name, slug: row.slug, role: row.role, planCode: typeof row.planCode === 'string' ? row.planCode : undefined, planStatus: typeof row.planStatus === 'string' ? row.planStatus : undefined, planExpiresAt: typeof row.planExpiresAt === 'string' ? row.planExpiresAt : null } satisfies ServerOrganization] : [];
    }) : [];
    return { user: { id: user.id, email: user.email, role: user.role }, organizations };
  }

  async getPublicShare(token: string, signal?: AbortSignal): Promise<PublicSharePayload> {
    const response = await fetch(`${this.baseUrl}/v1/public/shares/${encodeURIComponent(token)}`, { credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    if (typeof payload.title !== 'string' || !validateDocument(payload.document).valid) throw new Error('Compartilhamento público inválido.');
    return { title: payload.title, document: normalizeDocument(payload.document as DiagramDocument), allowCopy: payload.allowCopy === true, allowPdf: payload.allowPdf === true, expiresAt: typeof payload.expiresAt === 'string' ? payload.expiresAt : null, ownerLabel: typeof payload.ownerLabel === 'string' ? payload.ownerLabel : null };
  }

  async listPublicShares(token: string, projectId: string, signal?: AbortSignal): Promise<PublicShareSummary[]> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/public-shares`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.shares) ? payload.shares.flatMap((item) => {
      if (!item || typeof item !== 'object') return []; const row = item as Record<string, unknown>;
      if (typeof row.id !== 'string' || typeof row.createdAt !== 'string') return [];
      return [{ id: row.id, allowCopy: row.allowCopy === true, allowPdf: row.allowPdf === true, createdAt: row.createdAt, expiresAt: typeof row.expiresAt === 'string' ? row.expiresAt : null, views: Number(row.views ?? 0) } satisfies PublicShareSummary];
    }) : [];
  }

  async createPublicShare(token: string, projectId: string, input: { allowCopy: boolean; allowPdf: boolean; expiresInDays: number | null }, signal?: AbortSignal): Promise<{ token: string; share: PublicShareSummary }> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/public-shares`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response); const row = payload.share as Record<string, unknown> | undefined;
    if (typeof payload.token !== 'string' || !row || typeof row.id !== 'string' || typeof row.createdAt !== 'string') throw new Error('Link público inválido retornado pelo servidor.');
    return { token: payload.token, share: { id: row.id, allowCopy: row.allowCopy === true, allowPdf: row.allowPdf === true, createdAt: row.createdAt, expiresAt: typeof row.expiresAt === 'string' ? row.expiresAt : null, views: Number(row.views ?? 0) } };
  }

  async revokePublicShare(token: string, projectId: string, shareId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/public-shares/${encodeURIComponent(shareId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async listOrganizationTemplates(token: string, organizationId: string, signal?: AbortSignal): Promise<OrganizationTemplate[]> {
    const response = await fetch(`${this.baseUrl}/v1/organizations/${encodeURIComponent(organizationId)}/templates`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.templates) ? payload.templates.flatMap((item) => {
      if (!item || typeof item !== 'object') return []; const row = item as Record<string, unknown>;
      const template = row.template && typeof row.template === 'object' ? row.template as Record<string, unknown> : null;
      if (![row.id,row.name,row.description,row.createdAt,row.updatedAt,row.createdBy,row.createdByEmail].every((value) => typeof value === 'string') || !template || !Array.isArray(template.nodes) || !Array.isArray(template.edges)) return [];
      return [{ id: row.id as string, name: row.name as string, description: row.description as string, createdAt: row.createdAt as string, updatedAt: row.updatedAt as string, createdBy: row.createdBy as string, createdByEmail: row.createdByEmail as string, canDelete: row.canDelete === true, template: { nodes: template.nodes as DiagramNode[], edges: template.edges as DiagramEdge[] } } satisfies OrganizationTemplate];
    }) : [];
  }

  async createOrganizationTemplate(token: string, organizationId: string, input: { name: string; description: string; nodes: DiagramNode[]; edges: DiagramEdge[] }, signal?: AbortSignal): Promise<OrganizationTemplate> {
    const response = await fetch(`${this.baseUrl}/v1/organizations/${encodeURIComponent(organizationId)}/templates`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ name: input.name, description: input.description, template: { nodes: input.nodes, edges: input.edges } }), credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response); const row = payload.template as Record<string, unknown> | undefined; const template = row?.template && typeof row.template === 'object' ? row.template as Record<string, unknown> : null;
    if (!row || typeof row.id !== 'string' || typeof row.name !== 'string' || typeof row.description !== 'string' || typeof row.createdAt !== 'string' || typeof row.updatedAt !== 'string' || typeof row.createdBy !== 'string' || typeof row.createdByEmail !== 'string' || !template || !Array.isArray(template.nodes) || !Array.isArray(template.edges)) throw new Error('Modelo compartilhado inválido retornado pelo servidor.');
    return { id: row.id, name: row.name, description: row.description, createdAt: row.createdAt, updatedAt: row.updatedAt, createdBy: row.createdBy, createdByEmail: row.createdByEmail, canDelete: row.canDelete === true, template: { nodes: template.nodes as DiagramNode[], edges: template.edges as DiagramEdge[] } };
  }

  async updateOrganizationTemplate(token: string, organizationId: string, templateId: string, input: { name: string; description: string; nodes: DiagramNode[]; edges: DiagramEdge[] }, signal?: AbortSignal): Promise<OrganizationTemplate> {
    const response = await fetch(`${this.baseUrl}/v1/organizations/${encodeURIComponent(organizationId)}/templates/${encodeURIComponent(templateId)}`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ name: input.name, description: input.description, template: { nodes: input.nodes, edges: input.edges } }), credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response); const row = payload.template as Record<string, unknown> | undefined; const template = row?.template && typeof row.template === 'object' ? row.template as Record<string, unknown> : null;
    if (!row || typeof row.id !== 'string' || typeof row.name !== 'string' || typeof row.description !== 'string' || typeof row.createdAt !== 'string' || typeof row.updatedAt !== 'string' || typeof row.createdBy !== 'string' || typeof row.createdByEmail !== 'string' || !template || !Array.isArray(template.nodes) || !Array.isArray(template.edges)) throw new Error('Modelo compartilhado inválido retornado pelo servidor.');
    return { id: row.id, name: row.name, description: row.description, createdAt: row.createdAt, updatedAt: row.updatedAt, createdBy: row.createdBy, createdByEmail: row.createdByEmail, canDelete: row.canDelete === true, template: { nodes: template.nodes as DiagramNode[], edges: template.edges as DiagramEdge[] } };
  }

  async deleteOrganizationTemplate(token: string, organizationId: string, templateId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/organizations/${encodeURIComponent(organizationId)}/templates/${encodeURIComponent(templateId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async createOrganization(token: string, name: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/organizations`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ name: name.trim() }), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async updateOrganization(token: string, organizationId: string, input: { name: string; slug: string }, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/organizations/${encodeURIComponent(organizationId)}`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async adminOverview(token: string, signal?: AbortSignal): Promise<AdminOverview> {
    const response = await fetch(`${this.baseUrl}/v1/admin/overview`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    const statsRow = payload.stats && typeof payload.stats === 'object' ? payload.stats as Record<string, unknown> : {};
    const stats: AdminOverview['stats'] = { users: Number(statsRow.users) || 0, organizations: Number(statsRow.organizations) || 0, projects: Number(statsRow.projects) || 0, sessions: Number(statsRow.sessions) || 0, revisions: Number(statsRow.revisions) || 0, comments: Number(statsRow.comments) || 0, templates: Number(statsRow.templates) || 0, announcements: Number(statsRow.announcements) || 0, storageBytes: Number(statsRow.storageBytes) || 0 };
    const organizations = Array.isArray(payload.organizations) ? payload.organizations.flatMap((item) => {
      if (!item || typeof item !== 'object') return []; const row = item as Record<string, unknown>;
      if (typeof row.id !== 'string' || typeof row.name !== 'string' || typeof row.slug !== 'string' || typeof row.createdAt !== 'string') return [];
      return [{ id: row.id, name: row.name, slug: row.slug, createdAt: row.createdAt, members: Number(row.members) || 0, projects: Number(row.projects) || 0, storageBytes: Number(row.storageBytes) || 0, maxUsers: Number(row.maxUsers) || 25, maxProjects: Number(row.maxProjects) || 100, maxStorageBytes: Number(row.maxStorageBytes) || 1073741824, planCode: typeof row.planCode === 'string' ? row.planCode : 'business', planStatus: typeof row.planStatus === 'string' ? row.planStatus : 'active', planExpiresAt: typeof row.planExpiresAt === 'string' ? row.planExpiresAt : null }];
    }) : [];
    const users = Array.isArray(payload.users) ? payload.users.flatMap((item) => {
      if (!item || typeof item !== 'object') return []; const row = item as Record<string, unknown>;
      if (typeof row.id !== 'string' || typeof row.email !== 'string' || typeof row.role !== 'string' || typeof row.createdAt !== 'string' || typeof row.organizationId !== 'string' || typeof row.organizationName !== 'string' || typeof row.organizationRole !== 'string') return [];
      return [{ id: row.id, email: row.email, displayName: typeof row.displayName === 'string' ? row.displayName : '', role: row.role, enabled: row.enabled !== 0 && row.enabled !== false, createdAt: row.createdAt, organizationId: row.organizationId, organizationName: row.organizationName, organizationRole: row.organizationRole }];
    }) : [];
    const audit = Array.isArray(payload.audit) ? payload.audit.flatMap((item) => {
      if (!item || typeof item !== 'object') return []; const row = item as Record<string, unknown>;
      if (typeof row.id !== 'string' || typeof row.organizationId !== 'string' || typeof row.action !== 'string' || typeof row.createdAt !== 'string') return [];
      return [{ id: row.id, organizationId: row.organizationId, organizationName: typeof row.organizationName === 'string' ? row.organizationName : null, action: row.action, createdAt: row.createdAt, email: typeof row.email === 'string' ? row.email : null, projectTitle: typeof row.projectTitle === 'string' ? row.projectTitle : null, metadata: row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata) ? row.metadata as Record<string, unknown> : {} }];
    }) : [];
    return { stats, organizations, users, audit };
  }

  async listAdminPlans(token: string, signal?: AbortSignal): Promise<AdminPlan[]> {
    const response = await fetch(`${this.baseUrl}/v1/admin/plans`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.plans) ? payload.plans.flatMap((item) => {
      if (!item || typeof item !== 'object') return []; const row=item as Record<string, unknown>;
      if (typeof row.code !== 'string' || typeof row.name !== 'string') return [];
      return [{
        code: row.code, name: row.name, description: typeof row.description === 'string' ? row.description : '',
        monthlyPriceCents: row.monthlyPriceCents === null || row.monthlyPriceCents === undefined ? null : Number(row.monthlyPriceCents),
        annualPriceCents: row.annualPriceCents === null || row.annualPriceCents === undefined ? null : Number(row.annualPriceCents),
        maxUsers: Number(row.maxUsers) || 1, maxProjects: Number(row.maxProjects) || 1, maxStorageBytes: Number(row.maxStorageBytes) || 1024*1024,
        features: Array.isArray(row.features) ? row.features.map(String) : [], active: row.active !== false, system: row.system === true,
        createdAt: typeof row.createdAt === 'string' ? row.createdAt : '', updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : '',
      } satisfies AdminPlan];
    }) : [];
  }

  async createAdminPlan(token: string, input: Omit<AdminPlan,'createdAt'|'updatedAt'|'system'>, signal?: AbortSignal): Promise<AdminPlan> {
    const response = await fetch(`${this.baseUrl}/v1/admin/plans`, { method:'POST', headers:{ authorization:`Bearer ${token}`,'content-type':'application/json' }, body:JSON.stringify(input), credentials:'omit', cache:'no-store', signal });
    const payload=await ensureOk(response); return payload.plan as unknown as AdminPlan;
  }

  async updateAdminPlan(token: string, code: string, input: Partial<Omit<AdminPlan,'code'|'createdAt'|'updatedAt'|'system'>>, signal?: AbortSignal): Promise<AdminPlan> {
    const response = await fetch(`${this.baseUrl}/v1/admin/plans/${encodeURIComponent(code)}`, { method:'PUT', headers:{ authorization:`Bearer ${token}`,'content-type':'application/json' }, body:JSON.stringify(input), credentials:'omit', cache:'no-store', signal });
    const payload=await ensureOk(response); return payload.plan as unknown as AdminPlan;
  }

  async deleteAdminPlan(token: string, code: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/plans/${encodeURIComponent(code)}`, { method:'DELETE', headers:{ authorization:`Bearer ${token}` }, credentials:'omit', cache:'no-store', signal });
    await ensureOk(response);
  }

  async updateOrganizationLimits(token: string, organizationId: string, input: { maxUsers: number; maxProjects: number; maxStorageBytes: number }, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/organizations/${encodeURIComponent(organizationId)}/limits`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async deleteOrganization(token: string, organizationId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/organizations/${encodeURIComponent(organizationId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async createAdminUser(token: string, input: { email: string; password: string; role: 'admin' | 'user'; organizationId?: string; organizationRole?: 'admin' | 'member' }, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/users`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async updateAdminUser(token: string, userId: string, input: { enabled?: boolean; role?: 'admin' | 'user'; email?: string; displayName?: string; organizationId?: string; organizationRole?: 'owner'|'admin'|'member' }, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/users/${encodeURIComponent(userId)}`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async createPasswordResetLink(token: string, userId: string, expiresInHours = 24, signal?: AbortSignal): Promise<{ token: string; email: string; expiresAt: string }> {
    const response = await fetch(`${this.baseUrl}/v1/admin/users/${encodeURIComponent(userId)}/password-reset-link`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ expiresInHours }), credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    if (typeof payload.token !== 'string' || typeof payload.email !== 'string' || typeof payload.expiresAt !== 'string') throw new Error('Link de redefinição inválido retornado pelo servidor.');
    return { token: payload.token, email: payload.email, expiresAt: payload.expiresAt };
  }

  async revokeAdminUserSessions(token: string, userId: string, signal?: AbortSignal): Promise<number> {
    const response = await fetch(`${this.baseUrl}/v1/admin/users/${encodeURIComponent(userId)}/sessions`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Number(payload.revoked ?? 0);
  }

  async deleteAdminUser(token: string, userId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async updateOrganizationPlan(token: string, organizationId: string, input: { planCode: string; planStatus: string; planExpiresAt: string | null }, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/organizations/${encodeURIComponent(organizationId)}/plan`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async validatePasswordReset(token: string, signal?: AbortSignal): Promise<{ email: string; expiresAt: string }> {
    const response = await fetch(`${this.baseUrl}/v1/auth/password-reset/${encodeURIComponent(token)}`, { credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    if (payload.valid !== true || typeof payload.email !== 'string' || typeof payload.expiresAt !== 'string') throw new Error('Link de redefinição inválido.');
    return { email: payload.email, expiresAt: payload.expiresAt };
  }

  async resetPassword(token: string, newPassword: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/auth/password-reset/${encodeURIComponent(token)}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ newPassword }), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async account(token: string, signal?: AbortSignal): Promise<AccountInfo> {
    const response = await fetch(`${this.baseUrl}/v1/account`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return {
      user: payload.user as unknown as ServerUser,
      sessions: Array.isArray(payload.sessions) ? payload.sessions as AccountInfo['sessions'] : [],
      organizations: Array.isArray(payload.organizations) ? payload.organizations as AccountInfo['organizations'] : [],
    };
  }

  async updateProfile(token: string, displayName: string, signal?: AbortSignal): Promise<ServerUser> {
    const response = await fetch(`${this.baseUrl}/v1/account/profile`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ displayName }), credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return payload.user as unknown as ServerUser;
  }

  async changePassword(token: string, currentPassword: string, newPassword: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/account/password`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ currentPassword, newPassword }), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async logoutOtherSessions(token: string, signal?: AbortSignal): Promise<number> {
    const response = await fetch(`${this.baseUrl}/v1/account/sessions/logout-others`, { method: 'POST', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response); return Number(payload.revoked ?? 0);
  }

  async revokeSession(token: string, sessionId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/account/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async listNotifications(token: string, signal?: AbortSignal): Promise<{ unread: number; notifications: UserNotification[] }> {
    const response = await fetch(`${this.baseUrl}/v1/notifications`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return { unread: Number(payload.unread) || 0, notifications: Array.isArray(payload.notifications) ? payload.notifications as unknown as UserNotification[] : [] };
  }

  async markNotificationRead(token: string, id: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/notifications/${encodeURIComponent(id)}/read`, { method: 'POST', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async markAllNotificationsRead(token: string, signal?: AbortSignal): Promise<number> {
    const response = await fetch(`${this.baseUrl}/v1/notifications/read-all`, { method: 'POST', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response); return Number(payload.updated ?? 0);
  }

  async updateProjectPreferences(token: string, projectId: string, input: { favorite: boolean; folder: string; tags: string[] }, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/preferences`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async listProjects(token: string, signal?: AbortSignal): Promise<RemoteProjectSummary[]> {
    const response = await fetch(`${this.baseUrl}/v1/projects`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return parseRemoteProjects(payload.projects);
  }

  async listTrash(token: string, signal?: AbortSignal): Promise<RemoteProjectSummary[]> {
    const response = await fetch(`${this.baseUrl}/v1/projects/trash`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return parseRemoteProjects(payload.projects);
  }

  async trashProject(token: string, projectId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async restoreProject(token: string, projectId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/restore`, { method: 'POST', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async deleteProjectPermanently(token: string, projectId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/permanent`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async listAnnouncements(token: string, signal?: AbortSignal): Promise<Announcement[]> {
    const response = await fetch(`${this.baseUrl}/v1/announcements`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.announcements) ? payload.announcements.flatMap((item) => parseAnnouncement(item)) : [];
  }

  async dismissAnnouncement(token: string, id: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/announcements/${encodeURIComponent(id)}/dismiss`, { method: 'POST', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async listAdminAnnouncements(token: string, signal?: AbortSignal): Promise<AdminAnnouncement[]> {
    const response = await fetch(`${this.baseUrl}/v1/admin/announcements`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.announcements) ? payload.announcements.flatMap((item) => parseAdminAnnouncement(item)) : [];
  }

  async createAdminAnnouncement(token: string, input: { title: string; body: string; level: Announcement['level']; dismissible: boolean; active: boolean; startsAt: string; endsAt: string | null; organizationId: string | null }, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/announcements`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async updateAdminAnnouncement(token: string, id: string, input: Partial<{ title: string; body: string; level: Announcement['level']; dismissible: boolean; active: boolean; startsAt: string; endsAt: string | null; organizationId: string | null }>, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/announcements/${encodeURIComponent(id)}`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(input), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async deleteAdminAnnouncement(token: string, id: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/admin/announcements/${encodeURIComponent(id)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async restoreRevision(token: string, projectId: string, revision: string, signal?: AbortSignal): Promise<{ revision: string; document: DiagramDocument }> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/revisions/${encodeURIComponent(revision)}/restore`, { method: 'POST', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    if (typeof payload.revision !== 'string' || !payload.document) throw new Error('Servidor não retornou a versão restaurada.');
    const document = normalizeDocument(payload.document as DiagramDocument); const validation = validateDocument(document);
    if (!validation.valid) throw new Error('Versão restaurada retornada pelo servidor é inválida.');
    return { revision: payload.revision, document };
  }

  async listRevisions(token: string, projectId: string, signal?: AbortSignal): Promise<ServerRevisionSummary[]> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/revisions`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.revisions) ? payload.revisions.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const row = item as Record<string, unknown>;
      if (![row.revision,row.localRevision,row.title,row.createdAt,row.email].every((value) => typeof value === 'string')) return [];
      return [row as unknown as ServerRevisionSummary];
    }) : [];
  }

  async getRevision(token: string, projectId: string, revision: string, signal?: AbortSignal): Promise<{ revision: string; title: string; createdAt: string; document: DiagramDocument }> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/revisions/${encodeURIComponent(revision)}`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    if (typeof payload.revision !== 'string' || typeof payload.title !== 'string' || typeof payload.createdAt !== 'string' || !validateDocument(payload.document).valid) throw new Error('Revisão remota inválida.');
    return { revision: payload.revision, title: payload.title, createdAt: payload.createdAt, document: normalizeDocument(payload.document as DiagramDocument) };
  }

  async listAudit(token: string, projectId: string, signal?: AbortSignal): Promise<AuditEvent[]> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/audit`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.events) ? payload.events.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const row = item as Record<string, unknown>;
      if (typeof row.id !== 'string' || typeof row.action !== 'string' || typeof row.createdAt !== 'string') return [];
      return [{ id: row.id, action: row.action, createdAt: row.createdAt, email: typeof row.email === 'string' ? row.email : null, metadata: row.metadata && typeof row.metadata === 'object' ? row.metadata as Record<string, unknown> : {} }];
    }) : [];
  }

  async listComments(token: string, projectId: string, signal?: AbortSignal): Promise<ProjectComment[]> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/comments`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.comments) ? payload.comments.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const row = item as Record<string, unknown>;
      if (typeof row.id !== 'string' || typeof row.body !== 'string' || typeof row.createdAt !== 'string' || typeof row.userId !== 'string' || typeof row.email !== 'string') return [];
      return [{ id: row.id, nodeId: typeof row.nodeId === 'string' ? row.nodeId : null, body: row.body, createdAt: row.createdAt, resolvedAt: typeof row.resolvedAt === 'string' ? row.resolvedAt : null, userId: row.userId, email: row.email }];
    }) : [];
  }

  async addComment(token: string, projectId: string, body: string, nodeId: string | null, signal?: AbortSignal): Promise<ProjectComment> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/comments`, { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ body, nodeId }), credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response); const row = payload.comment as Record<string, unknown> | undefined;
    if (!row || typeof row.id !== 'string' || typeof row.body !== 'string' || typeof row.createdAt !== 'string' || typeof row.userId !== 'string' || typeof row.email !== 'string') throw new Error('Comentário inválido retornado pelo servidor.');
    return { id: row.id, nodeId: typeof row.nodeId === 'string' ? row.nodeId : null, body: row.body, createdAt: row.createdAt, resolvedAt: typeof row.resolvedAt === 'string' ? row.resolvedAt : null, userId: row.userId, email: row.email };
  }

  async resolveComment(token: string, projectId: string, commentId: string, resolved: boolean, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(commentId)}`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify({ resolved }), credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async deleteComment(token: string, projectId: string, commentId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(commentId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async listMembers(token: string, projectId: string, signal?: AbortSignal): Promise<ProjectMember[]> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/members`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    return Array.isArray(payload.members) ? payload.members.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const row = item as Record<string, unknown>;
      if (typeof row.userId !== 'string' || typeof row.email !== 'string' || !['view','comment','edit'].includes(String(row.permission))) return [];
      return [{ userId: row.userId, email: row.email, permission: row.permission as ProjectMember['permission'] }];
    }) : [];
  }

  async shareProject(token: string, projectId: string, email: string, permission: ProjectMember['permission'], signal?: AbortSignal): Promise<ProjectMember> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/share`, {
      method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), permission }), credentials: 'omit', cache: 'no-store', signal,
    });
    const payload = await ensureOk(response);
    const member = payload.member as Record<string, unknown> | undefined;
    if (!member || typeof member.userId !== 'string' || typeof member.email !== 'string' || !['view','comment','edit'].includes(String(member.permission))) throw new Error('Resposta de compartilhamento inválida.');
    return { userId: member.userId, email: member.email, permission: member.permission as ProjectMember['permission'] };
  }

  async revokeProjectMember(token: string, projectId: string, userId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(projectId)}/share/${encodeURIComponent(userId)}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    await ensureOk(response);
  }

  async getProject(token: string, id: string, signal?: AbortSignal): Promise<{ revision: string; permission: string; document: DiagramDocument }> {
    const response = await fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(id)}`, { headers: { authorization: `Bearer ${token}` }, credentials: 'omit', cache: 'no-store', signal });
    const payload = await ensureOk(response);
    if (typeof payload.revision !== 'string' || typeof payload.permission !== 'string' || !validateDocument(payload.document).valid) throw new Error('Projeto remoto inválido.');
    return { revision: payload.revision, permission: payload.permission, document: normalizeDocument(payload.document as DiagramDocument) };
  }
}

export function normalizeServerBaseUrl(value: string): string { return safeBaseUrl(value); }
